/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `Help` for the Caintra Connect frontend.
 * @module pages/Help
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import styles from "../styles/Help.module.css";

import { AppContext } from "../context/AppContext";
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";

import { ReactComponent as HelpIcon } from "../assets/icons/Help/TitleIcon.svg";
import VideoLibrary from "../components/VideoTutorialsSection/VideoLibrary.jsx";
import FAQSection from "../components/FAQSection/index.jsx";
import UserExperienceSection from "../components/UserExperienceSection/index.jsx";
import HelpContactInformation from "../components/HelpContactInformation/index.jsx";

import { use, useEffect } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import { getUserRoleData } from "../logic/eventsList/index.js";

const Help = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { changePermissions } = use(AppContext);

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    const userId = localStorage.getItem("userId");
    if (userId) {
      await getUserRoleData(userId, navigate, changePermissions);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.languages}>
          <NotificationBell navigate={navigate} />
          <LanguageSelector />
        </div>

        {/* Page title */}
        <div className={styles.headerInfoContainer}>
          <div className={styles.headerInfo}>
            <HelpIcon />
            <h1 className={styles.title}>{t("pages.help.header")}</h1>
          </div>
        </div>
      </div>

      <div className={styles.mainContent}>
        <div className={styles.videoTutorialsSection}>
          <VideoLibrary />
        </div>
        <div className={styles.helpLayout}>
          <div className={styles.faqSection}>
            <FAQSection />
          </div>
          <div className={styles.experienceSection}>
            <UserExperienceSection />
            <HelpContactInformation />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Help;
